/**
 * RmsAdminHandoverScreen.js
 * =========================
 * The guided Admin Handover screen (TDI-386, deliverable 2 of the TDI-379
 * EMIS handover spec): admin open a child from the Admin Handover list and
 * complete the whole EMIS handover from this one screen, with EMIS open on
 * their other monitor — send the outcome letter, copy each value in the order
 * EMIS asks for it, download the documents and tick each one off as filed.
 *
 * Styled to match the Documents view (rmsDocumentsDashboard.js) and through it
 * the ADHD assessment platform's design language: the same page gradient,
 * RmsTopNavBar breadcrumb, patient header card, white rounded-xl section
 * cards with numbered circles, and primary/outline button pairs.
 *
 * Sections, in the order EMIS asks (TDI-379 Product Definition):
 *   1. Send the outcome letter (same sign-off/send machinery as the Documents
 *      view; TDI-387 adds the leaflet confirmation)
 *   2. Find the patient in EMIS (NHS number; surname, first name, DOB)
 *   3. Referral source (referrer, their role, the school, organisation type)
 *   4. Referral received date (triageReadiness.markedAt — never createdAt)
 *   5. Documents for EMIS, in the pathway's format with "Added to EMIS"
 *      ticks: separate documents (ADHD, TDI-388) or one evidence pack with
 *      its EMIS comment (autism, TDI-389), the outcome letter its own row
 *   6. Triage decision (outcome, who signed off, when)
 *   7. Handover complete (TDI-391): the admin roles mark it once the letter
 *      has been sent, which moves the child to Accepted or Not accepted
 *
 * A combined referral is handed over per pathway: the identity sections are
 * shared, the letter, documents, decision and completion follow the selected
 * pathway (?pathway=, with a switcher on screen).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { CheckCircleIcon, DownloadIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { db, functions } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { canSendTriageLetter, RMS_STAFF_MANAGEMENT_ROLES } from '../../../constants/userTypes';
import { formatDateShort } from '../../../utils/timestampUtils';
import {
  pathwaysForConcern,
  pathwayAdminHandover,
  pathwayReviews,
  pathwayTriage,
  pathwayTriageDocuments,
  resolveActingPathway,
  PATHWAY_LABELS,
} from '../../utils/triagePathways';
import { getTriageDecisionSummary, isHandoverComplete } from '../../utils/referralStatus';
import { usesEvidencePack } from '../../constants/pathwaySettings';
import { leafletsForLetter } from '../../constants/letterLeaflets';
import {
  buildEmisFilename,
  emisNameParts,
  DOCUMENT_LABEL_LETTER,
} from '../../utils/emisFileNaming';
import {
  apiCompleteAdminHandover,
  apiGetReferralParticipants,
  apiPublishTriageLetter,
  apiSetAdminHandoverTick,
} from '../../utils/rmsApi';
import SendTriageLetterDialog, { availableLetterRecipients } from '../../components/provider/SendTriageLetterDialog';
import CompleteAdminHandoverDialog from '../../components/provider/CompleteAdminHandoverDialog';
import QuestionnaireDownloadsPanel from '../../components/provider/QuestionnaireDownloadsPanel';
import CopyButton from '../../components/CopyButton';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import ConsentWithdrawnDialog from '../../components/provider/ConsentWithdrawnDialog';
import useConsentWithdrawnStop from '../../hooks/useConsentWithdrawnStop';
import useConfidentialDownload from '../../hooks/useConfidentialDownload';

/**
 * A date as EMIS is keyed: dd/mm/yyyy. Takes a Firestore Timestamp, a Date or
 * an ISO date string; returns null rather than guessing when it cannot read
 * the value. Date-only ISO strings convert mechanically (no timezone shift).
 */
export function ukDate(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
  }
  const date = typeof value.toDate === 'function' ? value.toDate() : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
}

/**
 * The copyable values the screen shows, derived from the live record. Pure so
 * the derivations are testable: every value is a plain string or null, and
 * null renders as "Not recorded" with its copy button disabled.
 */
export function handoverValues(patientData) {
  const d = patientData || {};
  const subject = d.subject || {};
  const referrer = d.referrer || {};
  const school = d.school || {};
  const schoolName =
    school.schoolName || school.name || d.educationSetting || d.schoolName || null;
  return {
    nhsNumber: subject.nhsNumber || null,
    surname: subject.lastName || d.subject_last_name || d.lastName || null,
    firstName: subject.firstName || d.subject_first_name || d.firstName || null,
    dob: ukDate(subject.dob || d.subject_dob || d.dateOfBirth || d.dob || null),
    referrerName: referrer.fullName || null,
    // roleTitle is not written on referrer today; relationshipLabel carries
    // the role ("SENCo at ...") on school-made referrals.
    referrerRole: referrer.roleTitle || referrer.relationshipLabel || null,
    schoolName,
    organisationType: d.referrerType === 'school' || schoolName ? 'School' : null,
    receivedDate: ukDate(d.triageReadiness?.markedAt || null),
    // The approved pilot GP practice the school chose at referral start
    // (TDI-488) — ADHD and combined referrals only; null on older records.
    gpPractice: d.referralPathway?.gpPractice?.name || null,
  };
}

const SECTION_TITLES = [
  'Send the outcome letter',
  'Find the patient in EMIS',
  'Referral source',
  'Referral received date',
  'Documents for EMIS',
  'Triage decision',
  'Handover complete',
];

/** One white section card: numbered circle, title, body — the Documents
 * view's row anatomy lifted to section level. */
function HandoverSection({ number, children }) {
  return (
    // text-left matters on every card: App.css centres text globally, so each
    // container sets its own alignment, as the Documents view's panels do.
    <section
      aria-label={`${number}. ${SECTION_TITLES[number - 1]}`}
      className="max-w-6xl mx-auto mb-4 text-left bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-primary/30 bg-primary/5 text-primary flex items-center justify-center font-semibold">
          {number}
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{SECTION_TITLES[number - 1]}</h2>
      </div>
      {children}
    </section>
  );
}

/** One copyable value: label, the value (or "Not recorded"), and its copy
 * button, which copies the plain value only. */
function ValueRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <div className="min-w-0">
        <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
        {value ? (
          <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
        ) : (
          <p className="text-sm italic text-gray-500">Not recorded</p>
        )}
      </div>
      <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />
    </div>
  );
}

export default function RmsAdminHandoverScreen() {
  const { patientRecordId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [patientData, setPatientData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [listenerError, setListenerError] = useState(null);
  // TDI-475: a withdrawal mid-handover stops the admin too. The rules shut
  // this listener when the grant goes, so the error is the cue.
  const withdrawnStop = useConsentWithdrawnStop({ patientRecord: patientData, listenerError });

  // Live record listener — the same source the Documents view reads, so the
  // two screens can never disagree about the letter or the ticks.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    return onSnapshot(
      patientRef,
      (snap) => {
        setPatientData(snap.exists() ? snap.data() : null);
        setLoadError(false);
        setListenerError(null);
      },
      (error) => {
        console.error('Error in admin handover Firestore listener:', error);
        setListenerError(error);
        if (!String(error?.code || '').includes('permission-denied')) setLoadError(true);
      },
    );
  }, [orgId, patientRecordId]);

  // ── Pathway (C8): combined referrals are handed over per pathway ──────────
  const intakeConcern = patientData?.intakeConcern;
  const pathways = useMemo(() => pathwaysForConcern(intakeConcern), [intakeConcern]);
  const [pathwayChoice, setPathwayChoice] = useState(null);
  const effectivePathway = useMemo(() => {
    const routePathway = String(searchParams.get('pathway') || '').trim().toLowerCase();
    return (
      pathwayChoice
      || resolveActingPathway({ intakeConcern }, routePathway, claims?.specialism)
      || pathways[0]
      || null
    );
  }, [pathwayChoice, intakeConcern, searchParams, claims?.specialism, pathways]);

  const triageDocuments = useMemo(
    () => pathwayTriageDocuments(patientData, effectivePathway),
    [patientData, effectivePathway],
  );
  const values = useMemo(() => handoverValues(patientData), [patientData]);
  const patientName = [values.firstName, values.surname].filter(Boolean).join(' ').trim() || 'Unknown Patient';

  // ── Letter state (same signals as the Documents view) ─────────────────────
  const hasPdf = Boolean(triageDocuments?.documentPaths?.pdf);
  const isSignedOff = Boolean(triageDocuments?.signOff);
  const sharedRoles = useMemo(
    () => (Array.isArray(triageDocuments?.sharedRoles) ? triageDocuments.sharedRoles : []),
    [triageDocuments],
  );
  const sentLabel = useMemo(() => sharedRoles
    .map((r) => (r === 'guardian' ? 'parent/carer' : 'school representative'))
    .join(' and '), [sharedRoles]);
  const canSend = canSendTriageLetter(claims, effectivePathway);

  const [downloading, setDownloading] = useState(false);

  // Every download first shows the confidentiality warning (TDI-484);
  // viewing inline stays ungated — nothing is saved to the device.
  const { requestDownload, confirmDialog: downloadConfirmDialog } = useConfidentialDownload({
    orgId, patientRecordId, patientRecord: patientData,
  });

  const handleLetterOpen = useCallback(async (disposition) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const fn = httpsCallable(functions, 'rmsFetchTriageDocument', { timeout: 30000 });
      const result = await fn({
        orgId,
        patientRecordId,
        fileFormat: 'pdf',
        disposition,
        ...(effectivePathway ? { pathway: effectivePathway } : {}),
      });
      const url = result.data?.downloadUrl;
      if (url) window.open(url, '_blank');
      else toast.error('The letter could not be fetched. Please try again.');
    } catch (err) {
      console.error('Letter fetch failed:', err);
      toast.error('The letter could not be fetched. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [orgId, patientRecordId, effectivePathway, downloading]);

  // ── Send flow (mirrors the Documents view; TDI-387 swaps the dialog) ──────
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [participants, setParticipants] = useState([]);
  const openSendDialog = useCallback(async () => {
    setShowSendDialog(true);
    try {
      const res = await apiGetReferralParticipants({ orgId, patientRecordId });
      setParticipants(res?.participants || []);
    } catch (err) {
      console.error('Failed to load referral participants:', err);
      setParticipants([]);
    }
  }, [orgId, patientRecordId]);
  const handleSend = useCallback(async (recipients) => {
    if (sending || !recipients?.length) return;
    setSending(true);
    try {
      const res = await apiPublishTriageLetter({
        orgId, patientRecordId, recipients, pathway: effectivePathway || undefined,
      });
      const labels = (res?.sharedRoles || recipients)
        .map((r) => (r === 'guardian' ? 'parent/carer' : 'school representative'))
        .join(' and ');
      const sentLeaflets = Array.isArray(res?.leaflets) ? res.leaflets : [];
      toast.success(sentLeaflets.length > 0
        ? `Letter and leaflets sent to the ${labels}.`
        : `Letter sent to the ${labels}.`);
      setShowSendDialog(false);
    } catch (err) {
      console.error('Failed to send triage letter:', err);
      toast.error(err?.message || 'Could not send the letter. Please try again.');
    } finally {
      setSending(false);
    }
  }, [orgId, patientRecordId, effectivePathway, sending]);
  const recipientOptions = useMemo(() => availableLetterRecipients(participants), [participants]);

  // ── "Added to EMIS" ticks (saved progress, stored on the record) ──────────
  const canTickEmis = RMS_STAFF_MANAGEMENT_ROLES.includes(claims?.userType);
  const emisTicks = pathwayAdminHandover(patientData, effectivePathway)?.ticks || {};
  const [pendingTickKey, setPendingTickKey] = useState(null);
  const handleToggleTick = useCallback(async (docKey, ticked) => {
    if (pendingTickKey) return;
    setPendingTickKey(docKey);
    try {
      await apiSetAdminHandoverTick({
        orgId, patientRecordId, docKey, ticked, pathway: effectivePathway || undefined,
      });
    } catch (err) {
      console.error('Failed to record handover progress:', err);
      toast.error(err?.message || 'Could not save the tick. Please try again.');
    } finally {
      setPendingTickKey(null);
    }
  }, [orgId, patientRecordId, effectivePathway, pendingTickKey]);

  // ── Documents for EMIS, set by the pathway (TDI-385/388/389) ─────────────
  // Separate documents (ADHD): every row carries its EMIS file name, and the
  // outcome letter joins the list as its own last row, downloaded through the
  // letter fetch and ticked like any other document.
  // Evidence pack (autism): one pack row with its EMIS file name and the EMIS
  // comment the last download cached on the record (per pathway on a
  // combined referral), then the triage decision and the outcome letter.
  const separateDocuments = !usesEvidencePack(orgId, effectivePathway);
  const evidencePackComment =
    pathwayAdminHandover(patientData, effectivePathway)?.evidencePack?.emisComment || null;
  const letterDocumentRow = useMemo(() => {
    const parts = emisNameParts(patientData);
    const signOffAt = triageDocuments?.signOff?.signedOffAt || triageDocuments?.sentAt || null;
    return {
      key: 'letter',
      title: 'Outcome letter',
      detail: hasPdf
        ? (isSignedOff ? 'Signed off' : 'Rendered, awaiting sign-off')
        : 'Not prepared yet',
      // TDI-693: only a signed-off letter can be downloaded or filed. Before
      // that the row reads "Not available", with no download and no tick.
      available: hasPdf && isSignedOff,
      hideActionsUntilAvailable: true,
      filename: buildEmisFilename({
        dateValue: signOffAt,
        childName: parts.childName,
        documentLabel: DOCUMENT_LABEL_LETTER,
        schoolName: parts.schoolName,
        referrerName: parts.referrerName,
        referrerRole: parts.referrerRole,
      }),
      // TDI-484: the letter row saves a file, so it goes through the same
      // confidentiality confirm as every other download on this screen.
      onDownload: () => requestDownload(() => handleLetterOpen('attachment')),
      downloading,
    };
  }, [patientData, triageDocuments, hasPdf, isSignedOff, handleLetterOpen, requestDownload, downloading]);

  // ── Triage decision summary ───────────────────────────────────────────────
  const decision = useMemo(() => {
    const reviews = pathwayReviews(patientData, effectivePathway);
    const triage = pathwayTriage(patientData, effectivePathway);
    const { outcome } = getTriageDecisionSummary(reviews, {
      triageComplete: triage?.status === 'complete',
    });
    return outcome;
  }, [patientData, effectivePathway]);
  const signOff = triageDocuments?.signOff || null;

  // The leaflets that go with this letter (TDI-387): by the pathway's
  // settings and the decision, so the confirmation names exactly what the
  // server will record. After the send, what went out is read off the record.
  const leafletsToSend = useMemo(
    () => leafletsForLetter(orgId, effectivePathway, decision),
    [orgId, effectivePathway, decision],
  );
  const leafletsSent = Array.isArray(triageDocuments?.leaflets) ? triageDocuments.leaflets : [];

  // ── Handover complete (TDI-391) ───────────────────────────────────────────
  // The admin roles finish the handover, the same people who send the letter;
  // it becomes available once the letter has gone, and records who and when.
  // A letter sent before the step existed (the cutover) was finished then.
  const adminHandover = pathwayAdminHandover(patientData, effectivePathway) || {};
  const letterSent = Boolean(triageDocuments?.sentAt) || sharedRoles.length > 0;
  const handoverCompletedAt = adminHandover.completedAt || null;
  const handoverCompletedOn = handoverCompletedAt ? formatDateShort(handoverCompletedAt, '') : '';
  const finishedBeforeStep = !handoverCompletedAt && letterSent
    && isHandoverComplete(patientData, effectivePathway);
  const canCompleteHandover = canSend;
  // Named from the recorded decision; never guessed when there is none.
  const handoverDestination = decision === 'accepted' ? 'Accepted'
    : decision === 'discharged' ? 'Not accepted'
      : 'Accepted or Not accepted';
  const childFirstName = values.firstName || 'This child';
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completing, setCompleting] = useState(false);
  const handleCompleteHandover = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const res = await apiCompleteAdminHandover({
        orgId, patientRecordId, pathway: effectivePathway || undefined,
      });
      if (res?.alreadyComplete) {
        const by = adminHandover.completedByName;
        toast.info(`Already marked complete${by ? ` by ${by}` : ''}.`);
      } else {
        toast.success(`Handover complete. ${childFirstName} has moved to ${handoverDestination}.`);
      }
      setShowCompleteDialog(false);
    } catch (err) {
      console.error('Failed to complete the handover:', err);
      toast.error(err?.message || 'Could not complete the handover. Please try again.');
    } finally {
      setCompleting(false);
    }
  }, [orgId, patientRecordId, effectivePathway, completing, adminHandover.completedByName,
    childFirstName, handoverDestination]);

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}${effectivePathway ? `?pathway=${effectivePathway}` : ''}` },
    { label: 'Admin handover', path: null },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-8">
        <ConsentWithdrawnDialog
          open={withdrawnStop.stopped}
          onLeave={() => navigate('/rms/provider/dashboard')}
        />
        {loadError && (
          <div className="max-w-6xl mx-auto mb-4 text-left flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3">
            <ExclamationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600" aria-hidden="true" />
            <p className="text-sm text-red-800">
              The referral could not be loaded. Check your connection and refresh the page.
            </p>
          </div>
        )}

        {!patientData && !loadError && (
          <div className="max-w-6xl mx-auto mb-4 text-left bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse space-y-3" aria-hidden="true">
              <div className="h-7 w-64 bg-gray-200 rounded" />
              <div className="h-4 w-96 bg-gray-100 rounded" />
              <div className="h-4 w-80 bg-gray-100 rounded" />
            </div>
            <span className="sr-only">Loading the referral…</span>
          </div>
        )}

        {patientData && (
          <>
            {/* Patient header card — the Documents view's header, with the
                received date beside the referrer (never createdAt here). */}
            <div className="max-w-6xl mx-auto text-left bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-8">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-1">{patientName}</h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {values.dob && (
                        <span>
                          <span className="text-gray-400">DOB:</span>{' '}
                          <span className="font-medium">{values.dob}</span>
                        </span>
                      )}
                      {effectivePathway && (
                        <span>
                          <span className="text-gray-400">Pathway:</span>{' '}
                          <span className="font-medium">{PATHWAY_LABELS[effectivePathway]}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-12 w-px bg-gray-200" />
                  {/* Left aligned, never centred (design rules): these columns
                      deliberately depart from the Documents view header, whose
                      centred metadata predates the rule. */}
                  <div className="flex gap-8">
                    {values.referrerName && (
                      <div className="text-left">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Referred by</p>
                        <p className="text-sm font-medium text-gray-800">{values.referrerName}</p>
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Received on</p>
                      <p className="text-sm font-medium text-gray-800">
                        {formatDateShort(patientData?.triageReadiness?.markedAt, 'Not recorded')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Pathway switcher — a combined referral is handed over per
                      pathway, each with its own letter, documents and ticks. */}
                  {pathways.length > 1 && (
                    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Pathway">
                      {pathways.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPathwayChoice(p)}
                          aria-pressed={effectivePathway === p}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            effectivePathway === p
                              ? 'bg-primary text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {PATHWAY_LABELS[p]}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/rms/provider/patient/${patientRecordId}${effectivePathway ? `?pathway=${effectivePathway}` : ''}`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Go to Patient Overview
                    <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            {/* 1 · Send the outcome letter */}
            <HandoverSection number={1}>
              {sentLabel ? (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-green-700 flex items-center gap-1.5">
                    <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
                    Sent to the {sentLabel}
                    {triageDocuments?.sentBy?.clinicianName ? ` by ${triageDocuments.sentBy.clinicianName}` : ''}
                    {triageDocuments?.sentAt ? ` on ${formatDateShort(triageDocuments.sentAt, '')}` : ''}
                    {leafletsSent.length > 0
                      ? `, with ${leafletsSent.map((l) => l.title).join(' and ')}`
                      : ''}.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleLetterOpen('inline')}
                      disabled={!hasPdf || downloading}
                      className="px-4 py-2 text-sm font-semibold rounded-md bg-white text-primary border border-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      View letter
                    </button>
                    <button
                      type="button"
                      onClick={() => requestDownload(() => handleLetterOpen('attachment'))}
                      // A letter sent before sign-off existed downloads only
                      // once it is signed off (TDI-693, edge case 8).
                      disabled={!hasPdf || !isSignedOff || downloading}
                      title={hasPdf && !isSignedOff ? "You can download the letter once it's signed off" : undefined}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-gray-900 border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <DownloadIcon className="w-4 h-4" aria-hidden="true" />
                      Download letter
                    </button>
                  </div>
                </div>
              ) : !hasPdf ? (
                <p className="text-sm italic text-gray-600">
                  The outcome letter has not been prepared yet. It is written and signed off by the
                  clinical team; it can be sent from here once it is ready.
                </p>
              ) : !isSignedOff ? (
                <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                  <ExclamationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                  <p className="text-sm text-amber-800">
                    The letter must be signed off by a clinician before it can be sent. It becomes
                    sendable from here the moment it is signed off.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-gray-700">
                    The letter is signed off and ready to send to the parent/carer and school representative.
                    {!canSend && ' Only the support team or a non-clinical org admin can send it.'}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleLetterOpen('inline')}
                      disabled={downloading}
                      className="px-4 py-2 text-sm font-semibold rounded-md bg-white text-primary border border-primary hover:bg-primary/5 disabled:opacity-50 transition-colors"
                    >
                      View letter
                    </button>
                    {canSend && (
                      <button
                        type="button"
                        onClick={openSendDialog}
                        disabled={sending}
                        className="px-4 py-2 text-sm font-bold rounded-md bg-primary text-white hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
                      >
                        Send letter
                      </button>
                    )}
                  </div>
                </div>
              )}
            </HandoverSection>

            {/* 2 · Find the patient in EMIS */}
            <HandoverSection number={2}>
              <p className="text-sm text-gray-600 mb-3">
                Search EMIS by NHS number; the name and date of birth are for when that search fails.
              </p>
              <ValueRow label="NHS number" value={values.nhsNumber} />
              <ValueRow label="Surname" value={values.surname} />
              <ValueRow label="First name" value={values.firstName} />
              <ValueRow label="Date of birth" value={values.dob} />
              {values.gpPractice && (
                <ValueRow label="GP practice (school reported)" value={values.gpPractice} />
              )}
            </HandoverSection>

            {/* 3 · Referral source */}
            <HandoverSection number={3}>
              <ValueRow label="Referrer's name" value={values.referrerName} />
              <ValueRow label="Referrer's role" value={values.referrerRole} />
              <ValueRow label="Source organisation" value={values.schoolName} />
              <ValueRow label="Organisation type" value={values.organisationType} />
            </HandoverSection>

            {/* 4 · Referral received date — the date the referral moved into
                Ready for Triage, never the date it was started. */}
            <HandoverSection number={4}>
              <ValueRow label="Referral received date" value={values.receivedDate} />
            </HandoverSection>

            {/* 5 · Documents for EMIS, set by the pathway (pathwaySettings).
                Separate documents (ADHD, TDI-388): one row per document with
                its EMIS file name and copy button. Evidence pack (autism,
                TDI-389): one pack row with its file name and the EMIS comment,
                then the triage decision. Both list the outcome letter last. */}
            <HandoverSection number={5}>
              <QuestionnaireDownloadsPanel
                orgId={orgId}
                patientRecordId={patientRecordId}
                patientRecord={patientData}
                emisTicks={emisTicks}
                onToggleEmisTick={handleToggleTick}
                canTickEmis={canTickEmis}
                pendingTickKey={pendingTickKey}
                showEmisNames
                perFormDocuments={separateDocuments}
                evidencePack={separateDocuments
                  ? null
                  : { pathway: effectivePathway, emisComment: evidencePackComment }}
                trailingRows={[letterDocumentRow]}
              />
            </HandoverSection>

            {/* 6 · Triage decision */}
            <HandoverSection number={6}>
              <ValueRow
                label="Decision"
                value={decision === 'accepted' ? 'Accepted' : decision === 'discharged' ? 'Not accepted' : null}
              />
              <ValueRow label="Signed off by" value={signOff?.signedOffBy?.clinicianName || null} />
              <ValueRow
                label="Signed off on"
                value={signOff?.signedOffAt ? ukDate(signOff.signedOffAt) : null}
              />
            </HandoverSection>

            {/* 7 · Handover complete (TDI-391): disabled until the letter is
                sent; then the admin roles finish it, and everyone else is
                told who can. Once done, it says who finished it and when. */}
            <HandoverSection number={7}>
              {handoverCompletedAt ? (
                <p className="text-sm text-green-700 flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
                  Handover completed
                  {adminHandover.completedByName ? ` by ${adminHandover.completedByName}` : ''}
                  {handoverCompletedOn ? ` on ${handoverCompletedOn}` : ''}.
                </p>
              ) : finishedBeforeStep ? (
                <p className="text-sm text-gray-600">
                  This handover was finished when the letter was sent, before this step existed.
                </p>
              ) : (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-gray-600">
                    Completing the handover moves the child out of the Admin Handover list, to Accepted
                    or Not accepted. It becomes available once the letter has been sent.
                    {letterSent && !canCompleteHandover
                      && ' Only the support team or a non-clinical org admin can complete the handover.'}
                  </p>
                  {!letterSent ? (
                    <button
                      type="button"
                      disabled
                      title="Send the letter first"
                      className="px-4 py-2 text-sm font-bold rounded-md bg-gray-200 text-gray-400 cursor-not-allowed"
                    >
                      Mark handover complete
                    </button>
                  ) : canCompleteHandover && (
                    <button
                      type="button"
                      onClick={() => setShowCompleteDialog(true)}
                      disabled={completing}
                      className="px-4 py-2 text-sm font-bold rounded-md bg-primary text-white hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
                    >
                      Mark handover complete
                    </button>
                  )}
                </div>
              )}
            </HandoverSection>
          </>
        )}
      </div>

      <SendTriageLetterDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        recipients={recipientOptions}
        alreadyShared={sharedRoles}
        leaflets={leafletsToSend}
        sending={sending}
        onConfirm={handleSend}
      />

      <CompleteAdminHandoverDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        firstName={childFirstName}
        destination={handoverDestination}
        pathwayName={pathways.length > 1 ? (effectivePathway === 'asd' ? 'autism' : 'ADHD') : null}
        completing={completing}
        onConfirm={handleCompleteHandover}
      />

      {/* The confidentiality confirm every download passes through (TDI-484).
          The documents section's own downloads confirm inside the panel. */}
      {downloadConfirmDialog}
    </div>
  );
}
